import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormGroup,
  FormControlLabel,
} from '@mui/material';

import { useToast } from '../common/ToastProvider';
import apiService from '../../services/apiService'; // {success, data, error}
import api from '../../services/axiosConfig'; // Axios instance directly used for invite, apiService has no endpoint for invite

function Admin() {
  const { showSuccess, showError } = useToast();
  const [users, setUsers] = useState([]); // list of users from backend
  const [loading, setLoading] = useState(true); // whether the list of users is loaded
  const [error, setError] = useState(null); // did fetch throw an error
  const [actionLoading, setActionLoading] = useState(false); // whether to perform add/remove roles
  const [confirmMessage, setConfirmMessage] = useState(''); // text in popup
  const [confirmingAction, setConfirmingAction] = useState(null); // function after confirmation (Confirm Click)

  // Invite State
  const [openInviteDialog, setOpenInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  // const [inviteRoles, setInviteRoles] = useState([]);
  const [inviteRole, setInviteRole] = useState('admin');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');

  // Popup check
  const handleConfirm = (actionFn, message) => {
    setConfirmingAction(() => actionFn); //a function that is stored and executed later
    setConfirmMessage(message);
  };

  const confirmAction = async () => {
    if (confirmingAction) {
      setActionLoading(true); // Start loader
      try {
        await confirmingAction(); //performs the original function
      } catch (err) {
        console.error(err);
      } finally {
        setActionLoading(false); // Stop loader
      }
    }
    setConfirmingAction(null);
    setConfirmMessage('');
  };

  const cancelConfirm = () => {
    setConfirmingAction(null);
    setConfirmMessage('');
  };
  //

  // The function that sends the invitation
  const handleSendInvite = async () => {
    setInviteLoading(true);
    setInviteError('');
    setInviteSuccess('');

    try {
      await api.post('/api/invite-new-user', {
        email: inviteEmail,
        roles: [inviteRole], // Valid Roles -> {"owner", "admin", "member"}
      });

      setInviteSuccess('Invite sent successfully!');
      setInviteEmail('');
    } catch (error) {
      setInviteError(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setInviteLoading(false);
    }
  };
  //

  // Normalize backend roles, consistent format for UI
  const normalizeRoles = (roles) => {
    if (!roles) return [];
    return roles.map((r) => r.toLowerCase().trim());
  };

  // Fetch users
  const fetchAdminUsers = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiService.fetchAdminUsers();

      if (result.success) {
        const usersArray = Array.isArray(result.data)
          ? result.data
          : result.data?.users || [];

        setUsers(usersArray);
      } else {
        setError(result.error || 'Failed to fetch users');
      }
    } catch (err) {
      console.error(err);
      setError('Failed to fetch users');
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchAdminUsers();
  }, []);

  // Check roles
  const userHasAnyRole = (user) => {
    const roles = normalizeRoles(user.roles || []);

    return roles.includes('admin') || roles.includes('owner');
  };

  // Remove roles
  const handleRemoveRoles = async (userId) => {
    setActionLoading(true);

    try {
      const result = await apiService.removeRolesFromUser(userId);

      if (result.success) {
        showSuccess('Roles removed successfully');
        await fetchAdminUsers();
      } else {
        showError(result.error || 'Failed to remove roles');
      }
    } catch (err) {
      console.error(err);
      showError(err.response?.data?.detail || 'Failed to remove roles');
    }

    setActionLoading(false);
  };

  // Add roles
  const handleAddRoles = async (userId) => {
    setActionLoading(true);

    try {
      const result = await apiService.addRolesToUsers(userId, [
        'super_admin',
        'admin',
      ]);

      if (result.success) {
        showSuccess('Roles added successfully');
        await fetchAdminUsers();
      } else {
        showError(result.error || 'Failed to add roles');
      }
    } catch (err) {
      console.error(err);
      showError(err.response?.data?.detail || 'Failed to add roles');
    }

    setActionLoading(false);
  };

  // Render
  if (loading)
    return (
      <Box
        sx={{
          minHeight: '70vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );

  if (error)
    return (
      <Box sx={{ mt: 5 }}>
        <Alert severity='error'>{error}</Alert>
      </Box>
    );

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant='h5' sx={{ mb: 2 }}>
        Super Admin Users
      </Typography>

      {/* Invite Dialog */}
      <Box display='flex' justifyContent='flex-end' mb={2}>
        <Button
          variant='contained'
          color='primary'
          onClick={() => setOpenInviteDialog(true)}
        >
          Invite Admin
        </Button>
      </Box>

      <Dialog
        open={openInviteDialog}
        onClose={() => setOpenInviteDialog(false)}
      >
        <DialogTitle>Invite New User</DialogTitle>

        <DialogContent>
          {inviteError && (
            <Alert severity='error' sx={{ mb: 2 }}>
              {inviteError}
            </Alert>
          )}
          {inviteSuccess && (
            <Alert severity='success' sx={{ mb: 2 }}>
              {inviteSuccess}
            </Alert>
          )}

          <TextField
            fullWidth
            label='Email address'
            margin='dense'
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
          />

          {/* CheckBox with rolls that the user receives from the invite */}
          <Typography sx={{ mt: 2, mb: 1, fontWeight: 500 }}>
            Assigned Roles
          </Typography>

          <FormGroup row>
            <FormControlLabel
              control={<Checkbox checked disabled />}
              label='Super Admin'
            />
            <FormControlLabel
              control={<Checkbox checked disabled />}
              label='Admin'
            />
            <FormControlLabel
              control={<Checkbox checked disabled />}
              label='Member'
            />
          </FormGroup>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenInviteDialog(false)}>Cancel</Button>
          <Button
            variant='contained'
            disabled={inviteLoading}
            onClick={handleSendInvite}
          >
            {inviteLoading ? 'Sending...' : 'Send Invite'}
          </Button>
        </DialogActions>
      </Dialog>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Username</TableCell>
              <TableCell>First Name</TableCell>
              <TableCell>Last Name</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Roles</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {users.map((user) => {
              const hasRoles = userHasAnyRole(user);
              const roles = normalizeRoles(user.roles || []);

              return (
                <TableRow key={user.unique_id}>
                  <TableCell>{user.user_name}</TableCell>
                  <TableCell>{user.first_name}</TableCell>
                  <TableCell>{user.last_name}</TableCell>
                  <TableCell>{user.unique_id}</TableCell>

                  {/* Roles Cell */}
                  <TableCell>
                    {roles.length > 0
                      ? roles.map((r) => {
                          const roleLabel =
                            r === 'owner'
                              ? 'Owner'
                              : r === 'admin'
                                ? 'Admin'
                                : 'Member';
                          return (
                            <Box
                              key={r}
                              component='span'
                              sx={{
                                display: 'inline-block',
                                px: 1,
                                py: 0.25,
                                mr: 0.5,
                                borderRadius: 1,
                                bgcolor:
                                  r === 'owner'
                                    ? 'primary.main'
                                    : r === 'admin'
                                      ? 'success.main'
                                      : 'grey.400',
                                color: 'white',
                                fontSize: 12,
                              }}
                            >
                              {roleLabel}
                            </Box>
                          );
                        })
                      : '❌'}
                  </TableCell>

                  <TableCell>
                    <Button
                      variant='outlined'
                      color={hasRoles ? 'error' : 'primary'}
                      size='small'
                      onClick={() =>
                        hasRoles
                          ? handleConfirm(
                              () => handleRemoveRoles(user.unique_id),
                              'Are you sure you want to remove the rolls?',
                            )
                          : handleConfirm(
                              () => handleAddRoles(user.unique_id),
                              'Are you sure you want to add the rolls?',
                            )
                      }
                    >
                      {hasRoles ? 'Remove Roles' : 'Add Roles'}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Popup */}
      <Dialog open={!!confirmingAction} onClose={cancelConfirm}>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <p>{confirmMessage}</p>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={cancelConfirm}
            color='inherit'
            disabled={actionLoading}
          >
            Refuse
          </Button>
          <Button
            onClick={confirmAction}
            color='error'
            variant='contained'
            disabled={actionLoading}
            startIcon={
              actionLoading ? (
                <CircularProgress color='inherit' size={20} />
              ) : null
            }
          >
            {actionLoading ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Admin;
